// Leuchttisch: a tray of picked objects and a full-screen table that always fits every image on screen.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { Hit, ImageVersion } from '../api'
import { navigate } from '../router'

export type Picked = Pick<Hit, 'id' | 'inv' | 'title' | 'date' | 'images'> & { tech?: string }

type Props = {
  items: Picked[]
  onClear: () => void
  onRemove: (id: number) => void
  onReorder: (items: Picked[]) => void
  onDetails?: (id: number) => void
}

export function Tray({ items, onClear, onRemove, onReorder, onDetails }: Props) {
  // null: closed, -1: table, n: single view of items[n]
  const [view, setView] = useState<number | null>(null)
  if (!items.length) return null
  return (
    <>
      <div className="tray">
        <span><b>{items.length}</b> auf dem Leuchttisch</span>
        <div className="thumbs">
          {items.map((p, i) => p.images.small && (
            <button key={p.id} onClick={() => setView(i)} title={`${p.inv} – ${p.title}`}><img src={p.images.small.url} alt={p.title} /></button>
          ))}
        </div>
        <button className="solid" onClick={() => setView(-1)}>Leuchttisch öffnen</button>
        <button onClick={onClear}>Leeren</button>
      </div>
      {view != null && (
        <Table items={items} start={view} onClose={() => setView(null)} onRemove={onRemove} onReorder={onReorder}
          onDetails={onDetails && ((id) => { setView(null); onDetails(id) })} />
      )}
    </>
  )
}

// --- layout -------------------------------------------------------------------------------

const GAP = 14
const CAPTION = 38

function aspectOf(p: Picked, measured: Record<number, number>) {
  if (measured[p.id]) return measured[p.id]
  for (const v of [p.images.preview, p.images.huge, p.images.small]) {
    if (v?.width && v.height) return v.width / v.height
  }
  return 1.5
}

/** Split aspects (in order) into k contiguous rows with the smallest maximum row sum. */
function partition(aspects: number[], k: number): number[][] {
  const n = aspects.length
  const pre = [0]
  for (const a of aspects) pre.push(pre[pre.length - 1] + a)
  const cost = Array.from({ length: k + 1 }, () => Array(n + 1).fill(Infinity))
  const cut = Array.from({ length: k + 1 }, () => Array(n + 1).fill(0))
  cost[0][0] = 0
  for (let r = 1; r <= k; r++) {
    for (let i = r; i <= n; i++) {
      for (let j = r - 1; j < i; j++) {
        const c = Math.max(cost[r - 1][j], pre[i] - pre[j])
        if (c < cost[r][i]) { cost[r][i] = c; cut[r][i] = j }
      }
    }
  }
  const rows: number[][] = []
  let i = n
  for (let r = k; r > 0; r--) {
    const j = cut[r][i]
    rows.unshift(Array.from({ length: i - j }, (_, x) => j + x))
    i = j
  }
  return rows
}

type Row = { idx: number[]; h: number }

/** Justified rows that fill the box: try every row count and keep the one with the largest images. */
function fit(aspects: number[], W: number, H: number, caption: number): Row[] {
  const n = aspects.length
  if (!n || W <= 0 || H <= 0) return []
  let best: { rows: Row[]; area: number } = { rows: [], area: -1 }
  for (let k = 1; k <= n; k++) {
    const rows = partition(aspects, k).map((idx) => {
      const sum = idx.reduce((s, i) => s + aspects[i], 0)
      return { idx, sum, h: (W - GAP * (idx.length - 1)) / sum }
    })
    const free = H - GAP * (k - 1) - caption * k
    if (free <= 40) break
    const total = rows.reduce((s, r) => s + r.h, 0)
    const scale = Math.min(1, free / total)
    const area = rows.reduce((s, r) => s + r.h * r.h * scale * scale * r.sum, 0)
    // a single image never needs more than the box; prefer fewer rows on ties
    if (area > best.area * 1.001) best = { rows: rows.map((r) => ({ idx: r.idx, h: Math.floor(r.h * scale) })), area }
  }
  return best.rows
}

function useBox(ref: React.RefObject<HTMLElement | null>) {
  const [box, setBox] = useState({ w: 0, h: 0 })
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => setBox({ w: el.clientWidth, h: el.clientHeight })
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref])
  return box
}

/** Smallest version that is still sharp at the given CSS height. */
function versionFor(p: Picked, cssHeight: number): ImageVersion | undefined {
  const need = cssHeight * (window.devicePixelRatio || 1)
  const { small, preview, huge } = p.images
  if (preview && (preview.height ?? 600) >= need * 0.9) return preview
  return huge ?? preview ?? small
}

// --- the table ----------------------------------------------------------------------------

const BACKDROPS = [
  { key: 'dark', label: 'Dunkel' },
  { key: 'grey', label: 'Grau' },
  { key: 'light', label: 'Hell' },
] as const
type Backdrop = (typeof BACKDROPS)[number]['key']

function Table({ items, start, onClose, onRemove, onReorder, onDetails }: {
  items: Picked[]; start: number; onClose: () => void; onRemove: (id: number) => void
  onReorder: (items: Picked[]) => void; onDetails?: (id: number) => void
}) {
  const [focus, setFocus] = useState<number | null>(start >= 0 ? items[start]?.id ?? null : null)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [onlySelected, setOnlySelected] = useState(false)
  const [captions, setCaptions] = useState(true)
  const [backdrop, setBackdrop] = useState<Backdrop>(() => {
    try { return (localStorage.getItem('zint-lt-backdrop') as Backdrop) || 'dark' } catch { return 'dark' }
  })
  const [measured, setMeasured] = useState<Record<number, number>>({})
  const [drag, setDrag] = useState<number | null>(null)
  const stage = useRef<HTMLDivElement>(null)
  const box = useBox(stage)

  useEffect(() => { try { localStorage.setItem('zint-lt-backdrop', backdrop) } catch { /* ignore */ } }, [backdrop])
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  const visible = useMemo(() => {
    const sel = items.filter((p) => selected.has(p.id))
    return onlySelected && sel.length ? sel : items
  }, [items, selected, onlySelected])
  useEffect(() => { if (!items.length) onClose() }, [items.length, onClose])
  useEffect(() => { if (onlySelected && !items.some((p) => selected.has(p.id))) setOnlySelected(false) }, [items, selected, onlySelected])

  const aspects = useMemo(() => visible.map((p) => aspectOf(p, measured)), [visible, measured])
  const rows = useMemo(() => fit(aspects, box.w, box.h, captions ? CAPTION : 0), [aspects, box, captions])
  const focusIndex = focus == null ? -1 : visible.findIndex((p) => p.id === focus)

  const step = useCallback((d: number) => {
    if (focusIndex < 0) return
    setFocus(visible[(focusIndex + d + visible.length) % visible.length].id)
  }, [focusIndex, visible])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); if (focus != null) setFocus(null); else onClose() }
      else if (focus != null && e.key === 'ArrowRight') step(1)
      else if (focus != null && e.key === 'ArrowLeft') step(-1)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [focus, step, onClose])

  function toggle(id: number) {
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })
  }

  function remove(id: number) {
    setSelected((s) => { const n = new Set(s); n.delete(id); return n })
    if (focus === id) {
      const next = visible[focusIndex + 1] ?? visible[focusIndex - 1]
      setFocus(next && next.id !== id ? next.id : null)
    }
    onRemove(id)
  }

  function drop(targetId: number) {
    if (drag == null || drag === targetId) return
    const from = items.findIndex((p) => p.id === drag)
    const to = items.findIndex((p) => p.id === targetId)
    const next = [...items]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    onReorder(next)
    setDrag(null)
  }

  const nSel = items.filter((p) => selected.has(p.id)).length
  const focused = focusIndex >= 0 ? visible[focusIndex] : null

  return (
    <div className={`lt bd-${backdrop}`} role="dialog" aria-modal="true" aria-label="Leuchttisch">
      <header>
        <div className="lt-title">
          <b>Leuchttisch</b>
          <span className="muted">{focused ? `${focusIndex + 1} / ${visible.length}` : `${visible.length}${visible.length !== items.length ? ` von ${items.length}` : ''} ${items.length === 1 ? 'Objekt' : 'Objekte'}`}</span>
        </div>
        <div className="lt-tools">
          {focused ? (
            <button onClick={() => setFocus(null)}>← Alle</button>
          ) : (
            <>
              {nSel > 0 && (
                <>
                  <button aria-pressed={onlySelected} onClick={() => setOnlySelected((v) => !v)}>
                    {onlySelected ? 'Alle zeigen' : `Nur Auswahl vergleichen (${nSel})`}
                  </button>
                  <button onClick={() => { setSelected(new Set()); setOnlySelected(false) }}>Auswahl aufheben</button>
                </>
              )}
              <button aria-pressed={captions} onClick={() => setCaptions((v) => !v)}>Beschriftung</button>
            </>
          )}
          <span className="lt-bd" role="group" aria-label="Hintergrund">
            {BACKDROPS.map((b) => (
              <button key={b.key} className={`sw sw-${b.key}`} aria-pressed={backdrop === b.key} title={`Hintergrund ${b.label}`} aria-label={`Hintergrund ${b.label}`} onClick={() => setBackdrop(b.key)} />
            ))}
          </span>
          <button className="close" onClick={onClose} aria-label="Leuchttisch schließen">Schließen</button>
        </div>
      </header>

      {focused ? (
        <Single p={focused} count={visible.length} onStep={step} onRemove={() => remove(focused.id)} onDetails={onDetails} />
      ) : (
        <div className="lt-stage" ref={stage}>
          <div className="lt-rows">
            {rows.map((r) => (
              <div className="lt-row" key={r.idx.join('-')}>
                {r.idx.map((i) => {
                  const p = visible[i]
                  const w = Math.floor(r.h * aspects[i])
                  const img = versionFor(p, r.h)
                  const sel = selected.has(p.id)
                  return (
                    <figure key={p.id} className={`lt-tile${sel ? ' sel' : ''}${drag === p.id ? ' dragging' : ''}`} style={{ width: w }}
                      draggable onDragStart={(e) => { setDrag(p.id); e.dataTransfer.effectAllowed = 'move' }}
                      onDragOver={(e) => { if (drag != null) e.preventDefault() }} onDrop={() => drop(p.id)} onDragEnd={() => setDrag(null)}>
                      <button className="lt-img" style={{ height: r.h }} onClick={() => setFocus(p.id)} aria-label={`${p.title} groß anzeigen`}>
                        {img ? (
                          <img src={img.url} alt={p.title} draggable={false}
                            onLoad={(e) => {
                              const el = e.currentTarget
                              const a = el.naturalWidth / el.naturalHeight
                              if (a && Math.abs(a - aspects[i]) > 0.02) setMeasured((m) => ({ ...m, [p.id]: a }))
                            }} />
                        ) : <span className="noimg">ohne Vorschau</span>}
                      </button>
                      <button className={`lt-check${sel ? ' on' : ''}`} aria-pressed={sel} onClick={() => toggle(p.id)}
                        title={sel ? 'Aus Auswahl nehmen' : 'Zum Vergleich auswählen'} aria-label="Zum Vergleich auswählen">{sel ? '✓' : ''}</button>
                      <button className="lt-x" onClick={() => remove(p.id)} title="Vom Leuchttisch nehmen" aria-label="Vom Leuchttisch nehmen">×</button>
                      {captions && (
                        <figcaption style={{ height: CAPTION }}>
                          <span className="t">{p.title}</span>
                          <span className="mono">{p.inv}{p.date && ` · ${p.date}`}</span>
                        </figcaption>
                      )}
                    </figure>
                  )
                })}
              </div>
            ))}
          </div>
          {items.length > 1 && <p className="lt-hint">Bild anklicken: groß ansehen · ☐ auswählen und vergleichen · ziehen: Reihenfolge ändern</p>}
        </div>
      )}
    </div>
  )
}

function Single({ p, count, onStep, onRemove, onDetails }: {
  p: Picked; count: number; onStep: (d: number) => void; onRemove: () => void; onDetails?: (id: number) => void
}) {
  const [zoom, setZoom] = useState(false)
  const frame = useRef<HTMLDivElement>(null)
  const touch = useRef<number | null>(null)
  const img = p.images.huge ?? p.images.preview ?? p.images.small
  useEffect(() => setZoom(false), [p.id])

  function toggleZoom(e: React.MouseEvent<HTMLImageElement>) {
    const el = e.currentTarget
    const rx = (e.clientX - el.getBoundingClientRect().left) / el.clientWidth
    const ry = (e.clientY - el.getBoundingClientRect().top) / el.clientHeight
    setZoom((z) => !z)
    if (!zoom) {
      requestAnimationFrame(() => {
        const f = frame.current
        const big = f?.querySelector('img')
        if (!f || !big) return
        f.scrollLeft = rx * big.clientWidth - f.clientWidth / 2
        f.scrollTop = ry * big.clientHeight - f.clientHeight / 2
      })
    }
  }

  return (
    <div className="lt-single">
      <div ref={frame} className={`lt-frame${zoom ? ' zoom' : ''}`}
        onTouchStart={(e) => { touch.current = zoom ? null : e.touches[0].clientX }}
        onTouchEnd={(e) => {
          if (touch.current == null) return
          const dx = e.changedTouches[0].clientX - touch.current
          if (Math.abs(dx) > 50) onStep(dx < 0 ? 1 : -1)
          touch.current = null
        }}>
        {img ? <img key={p.id} src={img.url} alt={p.title} onClick={toggleZoom} title={zoom ? 'Einpassen' : 'Vergrößern'} />
          : <span className="noimg">ohne Vorschau</span>}
      </div>
      {count > 1 && (
        <>
          <button className="lt-nav prev" onClick={() => onStep(-1)} aria-label="Vorheriges Bild">‹</button>
          <button className="lt-nav next" onClick={() => onStep(1)} aria-label="Nächstes Bild">›</button>
        </>
      )}
      <footer>
        <div className="cap"><b>{p.title}</b><span className="mono">{p.inv}{p.date && ` · ${p.date}`}</span></div>
        <div className="acts">
          {onDetails && <button onClick={() => onDetails(p.id)}>Details</button>}
          <button onClick={() => navigate(`/lizenz/${p.id}`)}>Lizenz berechnen</button>
          <button onClick={onRemove}>Vom Leuchttisch nehmen</button>
        </div>
      </footer>
    </div>
  )
}
